import {
  getUploadPresignedUrl,
  uploadFileOnPresignedUrl,
  getDownloadPresignedUrl,
} from "../setup/api/services/file";
import { AWS_S3_BASE_URL, AWS_S3_BUCKET } from "./constants";
import { EUploadImageType, UploadProgress, IUploadResult } from "./enums";
import S3, { ManagedUpload } from "aws-sdk/clients/s3";

export const DOWNLOAD_URL_EXPIRATION = 604800; // 7 days

const s3 = new S3({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECURITY_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
});

/** Bucket key prefix per upload target. */
const keyPrefix = (type: EUploadImageType, id: string | number): string => {
  if (type === EUploadImageType.CONTRACT) return `images/contracts/${id}`;
  if (type === EUploadImageType.ORGANIZATION)
    return `images/organizations/${id}`;
  return `images/users/${id}`;
};

/**
 * Builds a unique object key, PDFs included.
 *
 * Previously PDFs were keyed on their original file name, which silently overwrote any earlier
 * upload of the same name under the same id — so two documents both called "scan.pdf" left two
 * records pointing at one object, having lost the first. Callers keep the original name
 * separately (e.g. an attachment's `description`), so nothing is lost by renaming the object.
 */
const buildObjectKey = (
  type: EUploadImageType,
  id: string | number,
  fileName: string
): string => {
  const lastDot = fileName.lastIndexOf(".");
  const ext = lastDot > 0 ? fileName.slice(lastDot + 1) : "";
  const unique = `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  return `${keyPrefix(type, id)}/${unique}${ext ? `.${ext}` : ""}`;
};

export const uploadFileWithPresignedUrl = async (
  file: File,
  type: EUploadImageType,
  id: string | number,
  progressCallback?: (progress: UploadProgress) => void
): Promise<IUploadResult> => {
  try {
    const params = {
      Bucket: AWS_S3_BUCKET,
      Key: buildObjectKey(type, id, file.name),
      Body: file,
      ACL: "public-read",
    };

    const presignedUrl = await getUploadPresignedUrl({
      bucket_name: params.Bucket,
      object_name: params.Key,
      expiration: 36000,
      is_public: true,
    });

    const response = await uploadFileOnPresignedUrl({
      presignedUrl,
      file,
      progressCallback,
    });
    console.log("response:", response);

    if (response.status !== 200) {
      throw new Error("Failed to upload file");
    }

    const downloadUrl = await getDownloadPresignedUrl({
      bucket_name: params.Bucket,
      object_name: params.Key,
      expiration: DOWNLOAD_URL_EXPIRATION,
    });

    return {
      url: downloadUrl,
      bucket: params.Bucket,
      key: params.Key,
    };
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const uploadFile = async (
  image: File,
  type: EUploadImageType,
  id: string | number,
  progressCallback?: (progess: ManagedUpload.Progress) => void
): Promise<string> => {
  try {
    const params = {
      Bucket: AWS_S3_BUCKET,
      // Same collision fix as uploadFileWithPresignedUrl. Note this also starts honouring the
      // ORGANIZATION prefix, which this function previously fell through to `images/users/`.
      Key: buildObjectKey(type, id, image.name),
      Body: image,
      ACL: "public-read",
    };
    return await new Promise((resolve) => {
      const uploadRequest = s3.upload(
        params,
        function (err: unknown, data: unknown) {
          if (err) {
            throw err;
          }
          resolve(`${AWS_S3_BASE_URL}${params.Key}`);
        }
      );
      if (progressCallback)
        uploadRequest?.on("httpUploadProgress", progressCallback);
    });
  } catch (err) {
    console.error(err);
    throw err;
  }
};
